// frontend/src/components/RelationshipManagement.js - Themed version with professional design
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Users,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Heart,
  Briefcase,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Bell,
  BellOff,
  Check,
  X
} from 'lucide-react';
import relationshipService from '../services/relationshipService';
import { formatDate } from '../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput } from './themed';
import { isAdmin } from '../utils/roleUtils';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';

const RelationshipManagement = ({ currentUser, currentEntity }) => {
    const { t } = useTranslation();
    const { background, text, cn } = useTheme();
    // Force re-render when preferences change
    useDateTimePreferences();
    
    const [loading, setLoading] = useState(true);
    const [relationships, setRelationships] = useState([]);
    const [filteredRelationships, setFilteredRelationships] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRelationType, setFilterRelationType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRelationship, setSelectedRelationship] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    
    // Edit form states
    const [editFormData, setEditFormData] = useState({
        relationType: '',
        isPrimary: false,
        notificationEnabled: true
    });
    const [editFormLoading, setEditFormLoading] = useState(false);

    // Add relationship form states
    const [addFormData, setAddFormData] = useState({
        userId: '',
        relatedUserId: '',
        relationType: '',
        isPrimary: false,
        notificationEnabled: true,
        userQuery: '', // For user lookup
        foundUser: null // For user lookup results
    });
    const [addFormLoading, setAddFormLoading] = useState(false);
    const [entityUsers, setEntityUsers] = useState([]);
    const [userLookupLoading, setUserLookupLoading] = useState(false);

    const relationTypes = relationshipService.getRelationTypes();
    const relationStatuses = relationshipService.getRelationStatuses();
    
    // Check if current user is admin to determine view mode
    const isUserAdmin = currentUser && isAdmin(currentUser.role);

    useEffect(() => {
        if (currentUser && currentUser.id && currentEntity && currentEntity.entityId) {
            fetchRelationships();
        }
    }, [currentUser, currentEntity]);

    useEffect(() => {
        filterRelationships();
    }, [relationships, searchTerm, filterRelationType, filterStatus]);

    /**
     * Fetch relationships based on user role - admin sees all entity relationships, users see their own
     */
    const fetchRelationships = async () => {
        if (!currentUser || !currentUser.id) {
            console.warn('No current user available');
            setLoading(false);
            return;
        }

        if (!currentEntity || !currentEntity.entityId) {
            console.warn('No current entity available');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            let response;
            
            if (isUserAdmin) {
                // Admin users see all relationships within the entity
                response = await relationshipService.getEntityRelationships(currentEntity.entityId);
            } else {
                // Regular users see only their own relationships
                response = await relationshipService.getUserRelationships(
                    currentEntity.entityId,
                    currentUser.id
                );
            }
            
            if (response.success) {
                const formattedRelationships = response.data.map(rel => 
                    relationshipService.formatRelationshipForDisplay(rel, currentUser.id)
                );
                setRelationships(formattedRelationships);
            } else {
                toast.error(t('errorFetchingRelationships'));
            }
        } catch (error) {
            console.error('Error fetching relationships:', error);
            toast.error(error.message || t('errorFetchingRelationships'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Filter relationships based on search and filter criteria
     */
    const filterRelationships = () => {
        let filtered = [...relationships];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(rel =>
                rel.relatedUserFullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                rel.relatedUserEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                rel.relationTypeLabel.toLowerCase().includes(searchTerm.toLowerCase()) ||
                rel.userFullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                rel.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Relation type filter
        if (filterRelationType !== 'all') {
            filtered = filtered.filter(rel => rel.relationType === filterRelationType);
        }

        // Status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(rel => rel.status === filterStatus);
        }

        setFilteredRelationships(filtered);
    };

    /**
     * Handle updating relationship status
     */
    const handleStatusUpdate = async (currentUser, currentEntity, relationshipId, newStatus) => {
        try {
            const response = await relationshipService.updateRelationshipStatus(currentUser, currentEntity, relationshipId, newStatus);
            
            if (response.success) {
                toast.success(t('relationshipStatusUpdated'));
                fetchRelationships(); // Refresh the list
            } else {
                toast.error(response.error || t('errorUpdatingRelationshipStatus'));
            }
        } catch (error) {
            console.error('Error updating relationship status:', error);
            toast.error(error.message || t('errorUpdatingRelationshipStatus'));
        }
    };

    /**
     * Handle opening edit modal and populating form
     */
    const handleEditRelationship = (relationship) => {
        setSelectedRelationship(relationship);
        setEditFormData({
            relationType: relationship.relationType,
            isPrimary: relationship.isPrimary,
            notificationEnabled: relationship.notificationEnabled
        });
        setShowEditModal(true);
    };

    /**
     * Handle saving updated relationship
     */
    const handleSaveEditRelationship = async () => {
        if (!selectedRelationship || !currentUser || !currentEntity) return;

        try {
            setEditFormLoading(true);
            
            const response = await relationshipService.updateRelationship(
                currentEntity.entityId,
                currentUser.id,
                selectedRelationship.id,
                editFormData
            );
            
            if (response.success) {
                toast.success(t('relationshipUpdated'));
                setShowEditModal(false);
                setSelectedRelationship(null);
                setEditFormData({
                    relationType: '',
                    isPrimary: false,
                    notificationEnabled: true
                });
                fetchRelationships(); // Refresh the list
            } else {
                toast.error(response.error || t('errorUpdatingRelationship'));
            }
        } catch (error) {
            console.error('Error updating relationship:', error);
            toast.error(error.message || t('errorUpdatingRelationship'));
        } finally {
            setEditFormLoading(false);
        }
    };

    /**
     * Handle canceling edit
     */
    const handleCancelEdit = () => {
        setShowEditModal(false);
        setSelectedRelationship(null);
        setEditFormData({
            relationType: '',
            isPrimary: false,
            notificationEnabled: true
        });
    };

    /**
     * Handle opening add modal and loading users for admin dropdown
     */
    const handleOpenAddModal = async () => {
        setAddFormData({
            userId: isUserAdmin ? '' : currentUser.id,
            relatedUserId: '',
            relationType: '',
            isPrimary: false,
            notificationEnabled: true,
            userQuery: '',
            foundUser: null
        });
        
        // Load entity users for admin dropdown
        if (isUserAdmin) {
            try {
                const response = await relationshipService.getEntityUsers(currentEntity.entityId);
                if (response.success) {
                    setEntityUsers(response.data);
                }
            } catch (error) {
                console.error('Error loading entity users:', error);
                toast.error(error.message || t('errorLoadingUsers'));
            }
        }
        
        setShowAddModal(true);
    };

    /**
     * Handle user lookup for non-admin users
     */
    const handleUserLookup = async () => {
        if (!addFormData.userQuery || addFormData.userQuery.length < 3) {
            toast.warn(t('searchQueryTooShort'));
            return;
        }

        try {
            setUserLookupLoading(true);
            const response = await relationshipService.lookupUser(currentEntity.entityId, addFormData.userQuery);
            
            if (response.success && response.data) {
                setAddFormData({
                    ...addFormData,
                    foundUser: response.data,
                    relatedUserId: response.data.id
                });
                toast.success(t('userFound', { name: response.data.full_name }));
            } else {
                setAddFormData({
                    ...addFormData,
                    foundUser: null,
                    relatedUserId: ''
                });
                toast.info(response.message || t('userNotFound'));
            }
        } catch (error) {
            console.error('Error looking up user:', error);
            toast.error(error.message || t('errorLookingUpUser'));
        } finally {
            setUserLookupLoading(false);
        }
    };

    /**
     * Handle creating relationship (admin flow) or relationship request (user flow)
     */
    const handleCreateRelationship = async () => {
        if (!addFormData.relatedUserId || !addFormData.relationType) {
            toast.error(t('pleaseSelectUserAndRelationType'));
            return;
        }

        try {
            setAddFormLoading(true);
            let response;

            if (isUserAdmin) {
                // Admin flow: Create immediate active relationship
                response = await relationshipService.createRelationship(
                    currentEntity.entityId,
                    parseInt(addFormData.userId),
                    {
                        relatedUserId: parseInt(addFormData.relatedUserId),
                        relationType: addFormData.relationType,
                        isPrimary: addFormData.isPrimary,
                        notificationEnabled: addFormData.notificationEnabled
                    }
                );
                toast.success(t('relationshipCreated'));
            } else {
                // User flow: Create pending relationship request
                response = await relationshipService.createRelationshipRequest(
                    currentEntity.entityId,
                    {
                        relatedUserId: parseInt(addFormData.relatedUserId),
                        relationType: addFormData.relationType,
                        isPrimary: addFormData.isPrimary,
                        notificationEnabled: addFormData.notificationEnabled
                    }
                );
                toast.success(response.message || t('relationshipRequestSent'));
            }

            if (response.success) {
                setShowAddModal(false);
                fetchRelationships(); // Refresh the list
            }
        } catch (error) {
            console.error('Error creating relationship:', error);
            toast.error(error.message || t('errorCreatingRelationship'));
        } finally {
            setAddFormLoading(false);
        }
    };

    /**
     * Handle canceling add modal
     */
    const handleCancelAdd = () => {
        setShowAddModal(false);
        setAddFormData({
            userId: '',
            relatedUserId: '',
            relationType: '',
            isPrimary: false,
            notificationEnabled: true,
            userQuery: '',
            foundUser: null
        });
        setEntityUsers([]);
    };

    /**
     * Handle deleting relationship
     */
    const handleDeleteRelationship = async () => {
        if (!selectedRelationship) return;

        try {
            setDeleteLoading(true);
            const response = await relationshipService.deleteRelationship(currentEntity.entityId, currentUser.id, selectedRelationship.id);
            
            if (response.success) {
                toast.success(t('relationshipDeleted'));
                setShowDeleteModal(false);
                setSelectedRelationship(null);
                fetchRelationships(); // Refresh the list
            } else {
                toast.error(response.error || t('errorDeletingRelationship'));
            }
        } catch (error) {
            console.error('Error deleting relationship:', error);
            toast.error(error.message || t('errorDeletingRelationship'));
        } finally {
            setDeleteLoading(false);
        }
    };

    /**
     * Handle approving or rejecting relationship request
     */
    const handleApproveRejectRequest = async (relationshipId, action) => {
        try {
            const response = await relationshipService.approveRelationshipRequest(
                currentEntity.entityId,
                relationshipId,
                action
            );
            
            if (response.success) {
                toast.success(response.message || t(`relationshipRequest${action === 'approve' ? 'Approved' : 'Rejected'}`));
                fetchRelationships(); // Refresh the list
            } else {
                toast.error(response.error || t('errorProcessingRelationshipRequest'));
            }
        } catch (error) {
            console.error('Error processing relationship request:', error);
            toast.error(error.message || t('errorProcessingRelationshipRequest'));
        }
    };

    /**
     * Get icon for relationship type
     */
    const getRelationTypeIcon = (relationType) => {
        switch (relationType) {
            case 'guardian':
            case 'parent':
            case 'grandparent':
            case 'sibling':
            case 'spouse':
                return Heart;
            case 'colleague':
                return Briefcase;
            case 'emergency_contact':
                return Shield;
            case 'friend':
                return Users;
            default:
                return Users;
        }
    };

    /**
     * Get icon for relationship status
     */
    const getStatusIcon = (status) => {
        switch (status) {
            case 'active':
                return CheckCircle;
            case 'pending':
                return Clock;
            case 'inactive':
                return XCircle;
            default:
                return XCircle;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!currentUser || !currentUser.id) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500">{t('noUserSelected')}</p>
            </div>
        );
    }

    if (!currentEntity || !currentEntity.entityId) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500">{t('noEntitySelected')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h3 className={cn("text-lg font-medium", text.primary)}>
                        {isUserAdmin ? t('userRelationships') : t('myRelationships')}
                    </h3>
                    <p className={cn("text-sm", text.muted)}>
                        {isUserAdmin 
                            ? t('manageUserRelationshipsDescription')
                            : t('manageYourRelationshipsDescription')
                        }
                    </p>
                </div>
                <ThemedButton
                    onClick={handleOpenAddModal}
                    icon={Plus}
                >
                    {t('addRelationship')}
                </ThemedButton>
            </div>

            {/* Filters and Search */}
            <ThemedCard>
                <ThemedCard.Body>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <ThemedInput
                            label={
                                <>
                                    <Search className="inline w-4 h-4 mr-1" />
                                    {t('search')}
                                </>
                            }
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={t('searchRelationships')}
                        />

                        <div>
                            <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                                <Filter className="inline w-4 h-4 mr-1" />
                                {t('filterByRelationType')}
                            </label>
                            <select
                                value={filterRelationType}
                                onChange={(e) => setFilterRelationType(e.target.value)}
                                className={cn(
                                    "w-full px-3 py-2 border rounded-md transition-all duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "bg-white dark:bg-gray-700",
                                    text.primary,
                                    "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                                    "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                                )}
                            >
                                <option value="all">{t('allRelationTypes')}</option>
                                {relationTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                                <Filter className="inline w-4 h-4 mr-1" />
                                {t('filterByStatus')}
                            </label>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className={cn(
                                    "w-full px-3 py-2 border rounded-md transition-all duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "bg-white dark:bg-gray-700",
                                    text.primary,
                                    "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                                    "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                                )}
                            >
                                <option value="all">{t('allStatuses')}</option>
                                {relationStatuses.map(status => (
                                    <option key={status.value} value={status.value}>{status.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </ThemedCard.Body>
            </ThemedCard>

            {/* Relationships Table */}
            <ThemedCard>
                <div className="overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className={cn("bg-gray-50 dark:bg-gray-700")}>
                            <tr>
                                <th className={cn("px-6 py-3 text-left text-xs font-medium uppercase tracking-wider", text.muted)}>
                                    {t('actions')}
                                </th>
                                <th className={cn("px-6 py-3 text-left text-xs font-medium uppercase tracking-wider", text.muted)}>
                                    {t('relatedPerson')}
                                </th>
                                <th className={cn("px-6 py-3 text-left text-xs font-medium uppercase tracking-wider", text.muted)}>
                                    {t('relationshipType')}
                                </th>
                                <th className={cn("px-6 py-3 text-left text-xs font-medium uppercase tracking-wider", text.muted)}>
                                    {t('status')}
                                </th>
                                <th className={cn("px-6 py-3 text-left text-xs font-medium uppercase tracking-wider", text.muted)}>
                                    {t('notifications')}
                                </th>
                                <th className={cn("px-6 py-3 text-left text-xs font-medium uppercase tracking-wider", text.muted)}>
                                    {t('createdDate')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className={cn("divide-y divide-gray-200 dark:divide-gray-700", background.secondary)}>
                            {filteredRelationships.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className={cn("px-6 py-4 text-center", text.muted)}>
                                        {relationships.length === 0 
                                            ? t('noRelationshipsFound')
                                            : t('noRelationshipsMatchFilter')
                                        }
                                    </td>
                                </tr>
                            ) : (
                                filteredRelationships.map((relationship) => {
                                    const RelationTypeIcon = getRelationTypeIcon(relationship.relationType);
                                    const StatusIcon = getStatusIcon(relationship.status);

                                    return (
                                        <tr key={relationship.id} className={cn("hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors")}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    {relationship.status === 'pending' && relationship.relatedUserId === currentUser.id ? (
                                                        /* Approval buttons for pending requests where current user is the target */
                                                        <>
                                                            <button
                                                                onClick={() => handleApproveRejectRequest(relationship.id, 'approve')}
                                                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 transition-colors"
                                                                title={t('approveRequest')}
                                                            >
                                                                <Check className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleApproveRejectRequest(relationship.id, 'reject')}
                                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                                                title={t('rejectRequest')}
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        /* Standard edit/activate/delete buttons for active relationships or admin view */
                                                        <>
                                                            <button
                                                                onClick={() => handleEditRelationship(relationship)}
                                                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                                                                title={t('editRelationship')}
                                                                disabled={relationship.status === 'pending'}
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </button>
                                                            {relationship.status !== 'pending' && (
                                                                <button
                                                                    onClick={() => {
                                                                        const newStatus = relationship.status === 'active' ? 'inactive' : 'active';
                                                                        handleStatusUpdate(currentUser, currentEntity, relationship.id, newStatus);
                                                                    }}
                                                                    className={`${relationship.status === 'active' 
                                                                        ? 'text-yellow-600 hover:text-yellow-900' 
                                                                        : 'text-green-600 hover:text-green-900'
                                                                    } dark:text-yellow-400 dark:hover:text-yellow-300 transition-colors`}
                                                                    title={relationship.status === 'active' ? t('deactivateRelationship') : t('activateRelationship')}
                                                                >
                                                                    {relationship.status === 'active' ? (
                                                                        <UserX className="w-4 h-4" />
                                                                    ) : (
                                                                        <UserCheck className="w-4 h-4" />
                                                                    )}
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            setSelectedRelationship(relationship);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                                        title={t('deleteRelationship')}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className={cn("text-sm font-medium", text.primary)}>
                                                        {isUserAdmin 
                                                            ? `${relationship.userFirstName} ${relationship.userLastName}`.trim()
                                                            : relationship.userFullName
                                                        }
                                                    </div>
                                                    <div className={cn("text-sm", text.muted)}>
                                                        {isUserAdmin ? t('isRelatedTo') : ""}
                                                    </div>
                                                    <div className={cn("text-sm font-medium", text.primary)}>
                                                        {isUserAdmin 
                                                            ? `${relationship.relatedUserFirstName} ${relationship.relatedUserLastName}`.trim()
                                                            : relationship.relatedUserFullName
                                                        }
                                                    </div>                                                    
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <RelationTypeIcon className={cn("w-4 h-4 mr-2", text.muted)} />
                                                    <span className={cn("text-sm", text.primary)}>
                                                        {relationship.relationTypeLabel}
                                                    </span>
                                                    {relationship.isPrimary && (
                                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                                                            {t('primary')}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <StatusIcon className={`w-4 h-4 mr-2 text-${relationship.statusColor}-500`} />
                                                    <span className={`text-sm text-${relationship.statusColor}-600 dark:text-${relationship.statusColor}-400`}>
                                                        {relationship.statusLabel}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    {relationship.notificationEnabled ? (
                                                        <>
                                                            <Bell className="w-4 h-4 mr-2 text-green-500" />
                                                            <span className="text-sm text-green-600 dark:text-green-400">
                                                                {t('enabled')}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <BellOff className={cn("w-4 h-4 mr-2", text.muted)} />
                                                            <span className={cn("text-sm", text.muted)}>
                                                                {t('disabled')}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={cn("px-6 py-4 whitespace-nowrap text-sm", text.muted)}>
                                                {formatDate(relationship.createdAt)}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </ThemedCard>

            {/* Relationships Summary */}
            {relationships.length > 0 && (
                <ThemedCard>
                    <ThemedCard.Body>
                        <h4 className={cn("text-sm font-medium mb-2", text.primary)}>
                            {t('relationshipsSummary')}
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <span className={text.muted}>{t('total')}:</span>
                                <span className={cn("ml-2 font-medium", text.primary)}>
                                    {relationships.length}
                                </span>
                            </div>
                            <div>
                                <span className={text.muted}>{t('active')}:</span>
                                <span className="ml-2 font-medium text-green-600 dark:text-green-400">
                                    {relationships.filter(r => r.status === 'active').length}
                                </span>
                            </div>
                            <div>
                                <span className={text.muted}>{t('family')}:</span>
                                <span className="ml-2 font-medium text-blue-600 dark:text-blue-400">
                                    {relationships.filter(r => ['guardian', 'parent', 'grandparent', 'sibling', 'spouse'].includes(r.relationType)).length}
                                </span>
                            </div>
                            <div>
                                <span className={text.muted}>{t('notificationsEnabled')}:</span>
                                <span className={cn("ml-2 font-medium", text.primary)}>
                                    {relationships.filter(r => r.notificationEnabled).length}
                                </span>
                            </div>
                        </div>
                    </ThemedCard.Body>
                </ThemedCard>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedRelationship && (
                <ConfirmDialogShell
                    isOpen={showDeleteModal}
                    onClose={() => {
                        if (!deleteLoading) {
                            setShowDeleteModal(false);
                            setSelectedRelationship(null);
                        }
                    }}
                    onConfirm={handleDeleteRelationship}
                    title={t('deleteRelationship')}
                    confirmLabel={t('delete')}
                    confirmVariant="danger"
                    confirmIcon={Trash2}
                    isLoading={deleteLoading}
                    isDisabled={deleteLoading}
                    showIcon={true}
                    iconVariant="danger"
                    closeOnBackdropClick={!deleteLoading}
                >
                    <div className="space-y-4">
                        <p className={cn("text-sm", text.muted)}>
                            {t('confirmDeleteRelationship', {
                                name: isUserAdmin
                                    ? `${selectedRelationship.userFirstName} ${selectedRelationship.userLastName} and ${selectedRelationship.relatedUserFirstName} ${selectedRelationship.relatedUserLastName}`
                                    : `${selectedRelationship.userFullName} and ${selectedRelationship.relatedUserFullName}`,
                                relation: selectedRelationship.relationTypeLabel
                            })}
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-400">
                            <AlertTriangle className="inline w-4 h-4 mr-1" />
                            {t('deleteRelationshipWarning')}
                        </p>
                    </div>
                </ConfirmDialogShell>
            )}

            {/* Add Relationship Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <ThemedCard className="max-w-lg w-full">
                        <ThemedCard.Header>
                            {isUserAdmin ? t('createRelationship') : t('requestRelationship')}
                        </ThemedCard.Header>
                        <ThemedCard.Body className="space-y-4">
                            {isUserAdmin ? (
                                /* Admin Flow: Select two users from dropdowns */
                                <>
                                    {/* User Selection (Admin) */}
                                    <div>
                                        <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                                            {t('primaryUser')} *
                                        </label>
                                        <select
                                            value={addFormData.userId}
                                            onChange={(e) => setAddFormData({
                                                ...addFormData,
                                                userId: e.target.value
                                            })}
                                            className={cn(
                                                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                                                "border-gray-300 dark:border-gray-600",
                                                "bg-white dark:bg-gray-700",
                                                text.primary,
                                                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                                                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                                            )}
                                            required
                                        >
                                            <option value="">{t('selectPrimaryUser')}</option>
                                            {entityUsers.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {`${user.first_name} ${user.last_name}`.trim()} ({user.username})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Related User Selection (Admin) */}
                                    <div>
                                        <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                                            {t('relatedUser')} *
                                        </label>
                                        <select
                                            value={addFormData.relatedUserId}
                                            onChange={(e) => setAddFormData({
                                                ...addFormData,
                                                relatedUserId: e.target.value
                                            })}
                                            className={cn(
                                                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                                                "border-gray-300 dark:border-gray-600",
                                                "bg-white dark:bg-gray-700",
                                                text.primary,
                                                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                                                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                                            )}
                                            required
                                        >
                                            <option value="">{t('selectRelatedUser')}</option>
                                            {entityUsers.filter(user => user.id.toString() !== addFormData.userId).map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {`${user.first_name} ${user.last_name}`.trim()} ({user.username})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            ) : (
                                /* User Flow: Enter username/email and lookup */
                                <>
                                    {/* User Lookup */}
                                    <div>
                                        <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                                            {t('findUser')} *
                                        </label>
                                        <div className="flex space-x-2">
                                            <ThemedInput
                                                type="text"
                                                value={addFormData.userQuery}
                                                onChange={(e) => setAddFormData({
                                                    ...addFormData,
                                                    userQuery: e.target.value,
                                                    foundUser: null,
                                                    relatedUserId: ''
                                                })}
                                                placeholder={t('enterUsernameOrEmail')}
                                                className="flex-1"
                                            />
                                            <ThemedButton
                                                onClick={handleUserLookup}
                                                disabled={userLookupLoading || !addFormData.userQuery || addFormData.userQuery.length < 3}
                                                loading={userLookupLoading}
                                                variant="secondary"
                                            >
                                                {userLookupLoading ? t('searching') : t('search')}
                                            </ThemedButton>
                                        </div>
                                        <p className={cn("text-xs mt-1", text.muted)}>
                                            {t('searchMinimum3Characters')}
                                        </p>
                                    </div>

                                    {/* Found User Display */}
                                    {addFormData.foundUser && (
                                        <div className={cn("p-3 rounded-md border", "border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900")}>
                                            <div className="flex items-center space-x-2">
                                                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                                <span className={cn("text-sm font-medium", text.primary)}>
                                                    {t('userFound')}:
                                                </span>
                                            </div>
                                            <p className={cn("text-sm mt-1", text.primary)}>
                                                {addFormData.foundUser.full_name} ({addFormData.foundUser.username})
                                            </p>
                                            <p className={cn("text-xs", text.muted)}>
                                                {addFormData.foundUser.email}
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Relationship Type */}
                            <div>
                                <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                                    {t('relationshipType')} *
                                </label>
                                <select
                                    value={addFormData.relationType}
                                    onChange={(e) => setAddFormData({
                                        ...addFormData,
                                        relationType: e.target.value
                                    })}
                                    className={cn(
                                        "w-full px-3 py-2 border rounded-md transition-all duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700",
                                        text.primary,
                                        "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                                        "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                                    )}
                                    required
                                >
                                    <option value="">{t('selectRelationshipType')}</option>
                                    {relationTypes.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Is Primary Checkbox */}
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="addIsPrimary"
                                    checked={addFormData.isPrimary}
                                    onChange={(e) => setAddFormData({
                                        ...addFormData,
                                        isPrimary: e.target.checked
                                    })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="addIsPrimary" className={cn("ml-2 text-sm", text.primary)}>
                                    {t('primaryRelationship')}
                                </label>
                            </div>

                            {/* Notification Enabled Checkbox */}
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="addNotificationEnabled"
                                    checked={addFormData.notificationEnabled}
                                    onChange={(e) => setAddFormData({
                                        ...addFormData,
                                        notificationEnabled: e.target.checked
                                    })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="addNotificationEnabled" className={cn("ml-2 text-sm", text.primary)}>
                                    {t('enableNotifications')}
                                </label>
                            </div>


                        </ThemedCard.Body>
                        <ThemedCard.Footer>
                            <div className="flex justify-end space-x-3">
                                <ThemedButton
                                    variant="secondary"
                                    onClick={handleCancelAdd}
                                    disabled={addFormLoading}
                                >
                                    {t('cancel')}
                                </ThemedButton>
                                <ThemedButton
                                    onClick={handleCreateRelationship}
                                    disabled={addFormLoading || !addFormData.relatedUserId || !addFormData.relationType}
                                    loading={addFormLoading}
                                >
                                    {addFormLoading 
                                        ? (isUserAdmin ? t('creating') : t('sending'))
                                        : (isUserAdmin ? t('createRelationship') : t('sendRequest'))
                                    }
                                </ThemedButton>
                            </div>
                        </ThemedCard.Footer>
                    </ThemedCard>
                </div>
            )}

            {showEditModal && selectedRelationship && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <ThemedCard className="max-w-md w-full">
                        <ThemedCard.Header>
                            {t('editRelationship')} : {isUserAdmin 
                                ? `${selectedRelationship.userFirstName} ${selectedRelationship.userLastName} - ${selectedRelationship.relatedUserFirstName} ${selectedRelationship.relatedUserLastName}`
                                : `${selectedRelationship.userFullName} - ${selectedRelationship.relatedUserFullName}`
                            }
                        </ThemedCard.Header>
                        <ThemedCard.Body className="space-y-4">
                            {/* Relationship Type */}
                            <div>
                                <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                                    {t('relationshipType')} *
                                </label>
                                <select
                                    value={editFormData.relationType}
                                    onChange={(e) => setEditFormData({
                                        ...editFormData,
                                        relationType: e.target.value
                                    })}
                                    className={cn(
                                        "w-full px-3 py-2 border rounded-md transition-all duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700",
                                        text.primary,
                                        "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                                        "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                                    )}
                                    required
                                >
                                    <option value="">{t('selectRelationshipType')}</option>
                                    {relationTypes.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Is Primary Checkbox */}
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="editIsPrimary"
                                    checked={editFormData.isPrimary}
                                    onChange={(e) => setEditFormData({
                                        ...editFormData,
                                        isPrimary: e.target.checked
                                    })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="editIsPrimary" className={cn("ml-2 text-sm", text.primary)}>
                                    {t('primaryRelationship')}
                                </label>
                            </div>

                            {/* Notification Enabled Checkbox */}
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="editNotificationEnabled"
                                    checked={editFormData.notificationEnabled}
                                    onChange={(e) => setEditFormData({
                                        ...editFormData,
                                        notificationEnabled: e.target.checked
                                    })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="editNotificationEnabled" className={cn("ml-2 text-sm", text.primary)}>
                                    {t('enableNotifications')}
                                </label>
                            </div>

                            {/* Relationship Info */}
                            <div className={cn("p-3 rounded-md", "bg-gray-50 dark:bg-gray-700")}>
                                <h4 className={cn("text-sm font-medium mb-1", text.primary)}>
                                    {t('relationshipDetails')}
                                </h4>
                                <p className={cn("text-xs", text.muted)}>
                                    {t('user')}: {isUserAdmin 
                                        ? `${selectedRelationship.userFirstName} ${selectedRelationship.userLastName}`.trim()
                                        : selectedRelationship.userFullName
                                    }
                                </p>
                                <p className={cn("text-xs", text.muted)}>
                                    {t('relatedUser')}: {isUserAdmin 
                                        ? `${selectedRelationship.relatedUserFirstName} ${selectedRelationship.relatedUserLastName}`.trim()
                                        : selectedRelationship.relatedUserFullName
                                    }
                                </p>
                                <p className={cn("text-xs", text.muted)}>
                                    {t('currentStatus')}: {selectedRelationship.statusLabel}
                                </p>
                            </div>
                        </ThemedCard.Body>
                        <ThemedCard.Footer>
                            <div className="flex justify-end space-x-3">
                                <ThemedButton
                                    variant="secondary"
                                    onClick={handleCancelEdit}
                                    disabled={editFormLoading}
                                >
                                    {t('cancel')}
                                </ThemedButton>
                                <ThemedButton
                                    onClick={handleSaveEditRelationship}
                                    disabled={editFormLoading || !editFormData.relationType}
                                    loading={editFormLoading}
                                >
                                    {editFormLoading ? t('saving') : t('save')}
                                </ThemedButton>
                            </div>
                        </ThemedCard.Footer>
                    </ThemedCard>
                </div>
            )}
        </div>
    );
};

export default RelationshipManagement;
